import * as React from "react";
import _ from 'lodash';
import { DebugEx } from "@Utils/DebugEx"
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { IWidgetThingsClass } from '../Utils/IWidgetThingsClass';
import { isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { WebThing, WebThingPort } from '@API/WebThing';
import { Plegma } from '@API/Api';
import { WebThingPortType } from '@API/WebNodeTypes';
import { Localization } from '@Primitive/Localization/Localization';
import { GetVideoPlayerLocalizationDictionary, VideoPlayerLocalizationTextMapKey } from './VideoPlayerLocalization';
import { JsonSchemaValidator } from '@Utils/JsonSchemaValidator';

const VideoPlayer = React.lazy(() => import("./VideoPlayerWidget"));
// =======================================================================================================
export interface VideoPlayerData {
    DefaultVideoURL: string;
    DefaultVideoType: string;
}
interface VideoPlayerWidgetSettingsState {
    WidgetData: VideoPlayerData;
}
import { SettingsList } from '@Primitive/SettingsList';
import { SettingsType } from '@Primitive/SettingsListType';

class VideoPlayerWidgetSettings extends React.Component<IWidgetSettingsProps<VideoPlayerData>, VideoPlayerWidgetSettingsState> {
    // ---------------------------------------------------------------------------------------------------
    constructor(props: IWidgetSettingsProps<VideoPlayerData>) {
        super(props);

        this.state = {
            WidgetData: _.cloneDeep(this.props.WidgetData)
        };

        // Bind functions
        this.saveData = this.saveData.bind(this);
    }
    // ---------------------------------------------------------------------------------------------------
    saveData() {
        if (isDefined(this.props.onWidgetDataUpdate))
            this.props.onWidgetDataUpdate(this.state.WidgetData);
    }
    // ------------------------------------------------------------------------------------------------
    render() {
        return <SettingsList
            AutoSave={true}
            Data={this.state.WidgetData}
            SettingsMap={[
                {
                    name: '',
                    elements: [
                        {
                            type: SettingsType.String,
                            label: "URL",
                            tooltip: 'URL of the video that can be displayed.',
                            dataPath: "DefaultVideoURL",
                        },
                        {
                            type: SettingsType.DropDown,
                            label: "Type",
                            tooltip: 'Video type. Available options (mp4, ogg, webm, mjpeg, youtube, application/x-mpegURL)',
                            dataPath: "DefaultVideoType",
                            items: [
                                { value: "video/mp4", label: "video/mp4" },
                                { value: "video/ogg", label: "video/ogg" },
                                { value: "video/webm", label: "video/webm" },
                                { value: "video/mjpeg", label: "video/mjpeg" },
                                { value: "video/youtube", label: "video/youtube" },
                                { value: "application/x-mpegURL", label: "application/x-mpegURL" }
                            ]
                        }
                    ]
                }
            ]}
            onSaveData={(data) => this.setState({ WidgetData: data }, () => this.saveData())}
            Toolbar={this.props.SettingListToolbar}
        />
    }
    // ---------------------------------------------------------------------------------------------------
}

// =======================================================================================================
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing } from "../../WidgetStructs";
import { IWidgetDataValidateResp } from "../../IWidgetDataValidateResp";
// ---------------------------------------------------------------------------------------------------
function SourcesJsonSchemeValidator() {
    return JsonSchemaValidator.GetInstance("VideoSourcesJsonSchemeValidator", {
        title: "VideoSourcesData",
        type: "array",
        items: {
            type: 'object',
            properties: {
                src: { type: "string" },
                type: { enum: ["video/mp4", "video/ogg", "video/webm", "video/mjpeg", "video/youtube", "application/x-mpegURL"] }
            },
            required: ["src"]
        }
    });
}
// ---------------------------------------------------------------------------------------------------
// Description interface
// ------------------------------------------------------------------------------------------------
export class VideoPlayerWidget extends IWidgetThingsClass<VideoPlayerData> {
    // ---------------------------------------------------------------------------------------------------
    public Render(key: string, width: number, height: number): React.ReactNode {
        return <WidgetsThingsStructs.LazyWidget >
            <VideoPlayer />
        </WidgetsThingsStructs.LazyWidget>
    }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): React.ReactElement<any> {
        const parent = this.Parent;
        return <VideoPlayerWidgetSettings
            key={"Settings-" + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)} // copy of the data and is return the copy in the update cb
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Ports
        let ports: WebThingPort[] = [];
        ports.push(new WebThingPort(
            {
                Name: "Sources",
                PortKey: "sources",
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.None,
                Description: "",
                State: ""
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues("sources", data)
        ));

        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): VideoPlayerData {
        return {
            DefaultVideoURL: "",
            DefaultVideoType: "video/mp4"
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return VideoPlayerWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let data: VideoPlayerData = VideoPlayerWidget.GetDefaultWidgetDataStatic();
        return {
            Title: "Video Player",
            WidgetTitle: "",
            Key: "VideoPlayerWidget",
            Type: "VideoPlayerWidget",
            Data: data,
            Description: "Video Player widget",
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + "/png/video.png"
        };
    }
    // ------------------------------------------------------------------------------------------------
    ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        return SourcesJsonSchemeValidator().DataValidateString(value);
    };
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        // todo: expose buttons in 'primitive' group && pass language messages to video.js player
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            return {
                [WidgetsThingsStructs.WidgetLocalizationKeys.Title]: this.Description.WidgetTitle
            };
        else
            return {};
    }
    // ------------------------------------------------------------------------------------------------
}
// ------------------------------------------------------------------------------------------------ +++ Localization
export class VideoPlayerLocalizationHelper {
    // ------------------------------------------------------------------------------------------------
    /**
     * Return label per each button key. 
     */
    public static LocalizeDictionary(localizationKey: string): any {
        // get default values
        const defaultValues = GetVideoPlayerLocalizationDictionary();

        // check for optional `localizationKey`
        if (!isDefined(localizationKey)) {
            // return default value
            return defaultValues;
        }

        // try get localization instance
        const localization = Localization.GetInstance(localizationKey);

        if (!isDefined(localization)) {
            DebugEx.TraceError("VideoPlayer: passed `localizationKey`: " + localizationKey + " but could not find localization instance!");
            // fallback to defaults
            return defaultValues;
        }

        // instance found; try get text map
        const map = localization.GetTextMap(VideoPlayerLocalizationTextMapKey);
        if (!isDefined(map)) {
            DebugEx.TraceError("VideoPlayer: passed `localizationKey`: " + localizationKey + " but could not get text map.");
            // fallback to defaults
            return defaultValues;
        }

        // ok! text map found! let's update response
        const res = defaultValues;
        _.forEach(map, (value, key) => {
            if (!IsNullOrEmpty(value))
                res[key] = value;
        });

        return res;
    }
}
// ------------------------------------------------------------------------------------------------ --- Localization
// =======================================================================================================
